<template>
  <div id="payment-days" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading payment days rules..."
    />

    <v-row v-else class="g-container-large" no-gutters>
      <v-dialog
        v-if="addOrEditDialog"
        v-model="addOrEditDialog"
        max-width="750px"
        persistent
        @click:outside="resetEdition"
        @keydown.esc="resetEdition"
      >
        <i-edit-payment-day
          :rule="editablePaymentDayRule"
          :allowed-payment-days="allowedPaymentDays"
          @cancel="resetEdition"
          @save="reload"
        />
      </v-dialog>

      <v-dialog
        v-if="confirmationDialog"
        v-model="confirmationDialog"
        width="400px"
        @click:outside="resetDeletion"
        @keydown.esc="resetDeletion"
      >
        <i-confirmation-dialog
          action-text="Delete"
          :submitting="deletingPaymentDayRule || updatingPaymentDayRule"
          :text="confirmationDialogText"
          @cancel="resetDeletion"
          @confirm="confirmDeletion"
        />
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>
        <template>
          <v-row>
            <v-col cols="12" md="3" lg="3">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary"
                :class="{ 'g-full-width': $vuetifyDisplay.smAndDown }"
                @click="addOrEditDialog = true"
              >
                Customise Payment Days
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="data-maintenance/payment-days"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'ACTIVE' }"
                @click="selectedStatus = 'ACTIVE'"
                >Active</v-btn
              >
            </v-col>
            <v-col cols="4" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': selectedStatus === 'UPCOMING'
                }"
                @click="selectedStatus = 'UPCOMING'"
                >Upcoming</v-btn
              >
            </v-col>
            <v-col cols="4" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'PAST' }"
                @click="selectedStatus = 'PAST'"
                >Past</v-btn
              >
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" lg="6">
              <i-local-filter
                :filterPlaceholder="'Search Payment Days by Target Type or Target(s) or Geography'"
                @filter="localFilter"
              />
            </v-col>
          </v-row>
        </template>

        <template>
          <v-row>
            <v-col cols="12">
              <template
                v-if="!loading && filteredPaymentDayRulesByStatus.length === 0"
              >
                <i-empty-results hideButton :title="emptyFilteredResultsText" />
              </template>

              <template v-else>
                <template v-if="$vuetifyDisplay.smAndDown">
                  <div
                    class="mt-0 pa-0"
                    v-for="item in filteredPaymentDayRulesByStatus"
                    :key="item.id"
                  >
                    <v-card class="rounded-2 pa-4 mb-6">
                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span
                            ><strong>{{ headers[0].text }}</strong></span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip
                            v-if="item.targetBannerGroups.length"
                            small
                            color="accent"
                            ><strong>BANNER</strong></v-chip
                          >
                          <v-chip
                            v-else-if="item.targetMultiStoreOperators.length"
                            small
                            color="primary"
                            ><strong>MSO</strong></v-chip
                          >
                          <v-chip v-else small color="secondary"
                            ><strong>{{
                              isILR ? 'CUSTOMER' : 'RETAILER'
                            }}</strong></v-chip
                          >
                        </v-col>
                      </v-row>

                      <v-divider class="my-2"></v-divider>

                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span
                            ><strong>{{ headers[1].text }}</strong></span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.targetBannerGroups.length">
                            <span
                              v-for="item in item.targetBannerGroups"
                              :key="item.bannerGroupId"
                            >
                              {{ item.bannerGroupName }}
                              <br />
                            </span>
                          </span>
                          <span
                            v-else-if="item.targetMultiStoreOperators.length"
                          >
                            <span
                              v-for="item in item.targetMultiStoreOperators"
                              :key="item.multiStoreOperatorId"
                            >
                              {{ item.multiStoreOperatorName }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetStores.length">
                            <span
                              v-for="item in item.targetStores"
                              :key="item.storeId"
                            >
                              {{ item.storeName }}
                              <br />
                            </span>
                          </span>
                          <span v-else> - </span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>{{ headers[2].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.targetStoreDeliveryAddressStates">
                            <span
                              v-for="item in item.targetStoreDeliveryAddressStates"
                              :key="item.state"
                            >
                              {{ item.state }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetGeography === 'STORE'">{{
                            isILR ? 'Customer' : 'Retailer'
                          }}</span>
                          <span v-else>{{
                            $filters.sentenceCase(item.targetGeography)
                          }}</span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>{{ headers[3].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>+{{ item.paymentDays }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[5].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>{{ $filters.formatDate(item.startDate) }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[6].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <div
                            class="pa-1"
                            style="display: inline-block"
                            :class="{
                              'i-status-block-red': item.endsToday,
                              'g-border-radius-s': item.endsToday
                            }"
                          >
                            <span>{{ $filters.formatDate(item.endDate) }}</span>
                          </div>
                        </v-col>
                      </v-row>

                      <template v-if="item.status !== 'PAST'">
                        <v-divider class="mt-2 mb-4"></v-divider>

                        <v-row no-gutters>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-blue g-full-width"
                              @click="editPaymentDayRule(item)"
                            >
                              <span>Edit Payment Days</span>
                              <v-icon small class="ml-1"
                                >mdi-circle-edit-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-red g-full-width"
                              :disabled="
                                item.endsToday || item.status === 'PAST'
                              "
                              @click="startDeletion(item)"
                            >
                              <span>Delete Payment Days</span>
                              <v-icon small class="ml-1"
                                >mdi-delete-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>
                    </v-card>
                  </div>
                </template>

                <template v-else>
                  <v-data-table
                    class="pt-2 elevation-1 i-data-table"
                    item-key="id"
                    :headers="headers"
                    :items="filteredPaymentDayRulesByStatus"
                    :items-per-page="-1"
                    :loading="loading"
                    :no-data-text="emptyFilteredResultsText"
                    :no-results-text="emptyFilteredResultsText"
                  >
                    <template #[`item.targetType`]="{ item }">
                      <v-chip
                        v-if="item.targetBannerGroups.length"
                        small
                        color="accent"
                        >BANNER</v-chip
                      >
                      <v-chip
                        v-else-if="item.targetMultiStoreOperators.length"
                        small
                        color="primary"
                        >MSO</v-chip
                      >
                      <v-chip v-else small color="secondary">{{
                        isILR ? 'CUSTOMER' : 'RETAILER'
                      }}</v-chip>
                    </template>

                    <template #[`item.targetValue`]="{ item }">
                      <span v-if="item.targetBannerGroups.length">
                        <span
                          v-for="item in item.targetBannerGroups"
                          :key="item.bannerGroupId"
                        >
                          {{ item.bannerGroupName }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetMultiStoreOperators.length">
                        <span
                          v-for="item in item.targetMultiStoreOperators"
                          :key="item.multiStoreOperatorId"
                        >
                          {{ item.multiStoreOperatorName }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetStores.length">
                        <span
                          v-for="item in item.targetStores"
                          :key="item.storeId"
                        >
                          {{ item.storeName }}
                          <br />
                        </span>
                      </span>
                      <span v-else> - </span>
                    </template>

                    <template #[`item.targetGeography`]="{ item }">
                      <span v-if="item.targetStoreDeliveryAddressStates">
                        <span
                          v-for="item in item.targetStoreDeliveryAddressStates"
                          :key="item.state"
                        >
                          {{ item.state }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetGeography === 'STORE'">{{
                        isILR ? 'Customer' : 'Retailer'
                      }}</span>
                      <span v-else>{{
                        $filters.sentenceCase(item.targetGeography)
                      }}</span>
                    </template>

                    <template #[`item.startDate`]="{ item }">
                      <span>{{ $filters.formatDate(item.startDate) }}</span>
                    </template>

                    <template #[`item.endDate`]="{ item }">
                      <div
                        class="pa-1"
                        :class="{
                          'i-status-block-red': item.endsToday,
                          'g-border-radius-s': item.endsToday
                        }"
                      >
                        <span>{{ $filters.formatDate(item.endDate) }}</span>
                      </div>
                    </template>

                    <template #[`item.paymentDays`]="{ item }">
                      <span class="p-payment-days">
                        {{ item.paymentDays }}
                      </span>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <v-btn
                        color="secondary"
                        :disabled="item.status === 'PAST'"
                        icon
                        v-tooltip="{
                          content: 'Edit',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="editPaymentDayRule(item)"
                      >
                        <v-icon>mdi-circle-edit-outline</v-icon>
                      </v-btn>

                      <v-btn
                        color="error"
                        :disabled="item.endsToday || item.status === 'PAST'"
                        icon
                        v-tooltip="{
                          content: 'Delete',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="startDeletion(item)"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </template>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  fetchPaymentDayRules,
  deletePaymentDayRule,
  updatePaymentDayRule
} from '@/services/paymentDays'
import FiltersHelper from '@/modules/dataMaintenance/helper/FiltersHelper'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iEditPaymentDay from '../components/EditPaymentDay.vue'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'

export default {
  name: 'payment-days',

  components: {
    iEmptyResults,
    iInstructions,
    iConfirmationDialog,
    iEditPaymentDay,
    iLocalFilter,
    iOverlay
  },

  data: () => ({
    confirmationDialog: false,
    confirmationDialogText: '',
    currentDate: moment().format('YYYY-MM-DD'),
    deletingPaymentDayRule: false,
    editablePaymentDayRule: {},
    filterText: '',
    headers: [
      {
        text: 'TARGET TYPE',
        value: 'targetType',
        width: 150,
        align: 'center',
        sortable: false
      },
      {
        text: 'TARGET(S)',
        value: 'targetValue',
        width: '20%',
        align: 'center',
        sortable: false
      },
      {
        text: 'GEOGRAPHY',
        value: 'targetGeography',
        width: 150,
        align: 'center'
      },
      { text: 'PAYMENT DAYS', value: 'paymentDays', align: 'center' },
      { text: 'START DATE', value: 'startDate', align: 'center' },
      { text: 'END DATE', value: 'endDate', align: 'center' },
      {
        text: 'ACTIONS',
        value: 'actions',
        width: '200px',
        align: 'center',
        sortable: false
      }
    ],
    loading: true,
    addOrEditDialog: false,
    paymentDayRules: [],
    allowedPaymentDays: Array.from({ length: 60 }, (_, i) => i + 1),
    selectedPaymentDayRule: null,
    selectedStatus: 'ACTIVE',
    updatingPaymentDayRule: false
  }),

  computed: {
    ...mapGetters('profile', ['defaultPaymentDays']),
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('tenant', ['isILR']),

    emptyFilteredResultsText() {
      return `No ${this.selectedStatus} Payment Days found`
    },

    mappedPaymentDayRules() {
      return this.paymentDayRules.map((rule) => {
        return {
          ...rule,
          endsToday: rule.endDate === this.currentDate
        }
      })
    },

    paymentDayRulesByStatus() {
      return this.mappedPaymentDayRules
        .filter((rule) => rule.status === this.selectedStatus)
        .sort((r1, r2) => {
          if (r1.status === 'ACTIVE')
            return r1.startDate > r2.startDate ? -1 : 1
          if (r1.status === 'UPCOMING')
            return r1.startDate < r2.startDate ? -1 : 1
          if (r1.status === 'PAST') return r1.endDate > r2.endDate ? -1 : 1
        })
    },

    filteredPaymentDayRulesByStatus() {
      const searchTextLowerCase = this.filterText.toLowerCase()

      return this.paymentDayRulesByStatus.filter((rule) => {
        return (
          rule.targetGeography.toLowerCase().includes(searchTextLowerCase) ||
          rule.targetStoreDeliveryAddressStates?.filter((s) =>
            s.state.toLowerCase().includes(searchTextLowerCase)
          ).length > 0 ||
          FiltersHelper.filterByTargetType(rule, searchTextLowerCase) ||
          FiltersHelper.filterByTargets(rule, searchTextLowerCase)
        )
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    // ***
    // Functions related to handling actions
    // ***

    startDeletion(item) {
      this.selectedPaymentDayRule = item
      this.confirmationDialogText =
        this.selectedPaymentDayRule.status === 'ACTIVE'
          ? 'Active Payment Days will end today (End Date auto updates)'
          : 'Confirm deletion of these Payment Days'
      this.confirmationDialog = true
    },

    confirmDeletion() {
      this.selectedPaymentDayRule.status === 'ACTIVE'
        ? this.endPaymentDayRule(this.selectedPaymentDayRule)
        : this.deletePaymentDayRule(this.selectedPaymentDayRule.id)
    },

    reload() {
      this.getPaymentDayRules()
      this.resetEdition()
    },

    resetDeletion() {
      this.confirmationDialog = false
      this.confirmationDialogText = ''
      this.selectedPaymentDayRule = null
    },

    resetEdition() {
      this.editablePaymentDayRule = {}
      this.addOrEditDialog = false
    },

    // ***
    // Functions related to coupons data
    // ***

    editPaymentDayRule(rule = {}) {
      this.editablePaymentDayRule = _.cloneDeep(rule)
      this.addOrEditDialog = true
    },

    deletePaymentDayRule(ruleId) {
      this.deletingPaymentDayRule = true
      this.addNotification({
        color: 'secondary',
        text: 'Deleting your Payment Days rule...'
      })

      deletePaymentDayRule(this.selectedSupplierId, ruleId)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Payment Days successfully deleted!'
          })
          this.getPaymentDayRules()
        })
        .catch((err) => {
          console.log(err)
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.deletingPaymentDayRule = false
          this.resetDeletion()
        })
    },

    endPaymentDayRule(rule) {
      this.updatingPaymentDayRule = true
      this.addNotification({
        color: 'secondary',
        text: 'Ending Payment Days...'
      })

      const payload = {
        endDate: this.currentDate,
        startDate: rule.startDate,
        paymentDays: rule.paymentDays,
        targetBannerGroupIds:
          rule.targetBannerGroups?.map(
            (bannerGroup) => bannerGroup.bannerGroupId
          ) || [],
        targetMultiStoreOperatorIds:
          rule.targetMultiStoreOperators?.map(
            (mso) => mso.multiStoreOperatorId
          ) || [],
        targetStoreIds: rule.targetStores?.map((store) => store.storeId) || [],
        targetStoreDeliveryAddressStates:
          rule.targetStoreDeliveryAddressStates?.map((state) => state.state) ||
          []
      }

      updatePaymentDayRule(
        this.selectedSupplierId,
        this.selectedPaymentDayRule.id,
        payload
      )
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Payment Days end date has been updated to today!'
          })
        })
        .catch((err) => {
          console.log(err)
          this.addNotification({
            color: 'error',
            text: 'Payment Days could not be updated, please try again...'
          })
        })
        .finally(() => {
          this.updatingPaymentDayRule = false
          this.resetDeletion()
          this.reload()
        })
    },

    getPaymentDayRules() {
      this.loading = true
      fetchPaymentDayRules(this.selectedSupplierId)
        .then((res) => {
          this.paymentDayRules = res
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Payment Days could not be loaded, please try again...'
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  mounted() {
    this.getPaymentDayRules()
  }
}
</script>

<style lang="scss" scoped>
#payment-days {
  .p-payment-days {
    font-size: 16px;
    font-weight: bold;
    color: $primary-color;
  }
}
</style>
